import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock } from "react-icons/fa";
import emailjs from "@emailjs/browser";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Helmet } from "react-helmet-async";

const Container = styled.section`
  display: flex;
    background-color: ${(props) => props.theme.colors.extralight};
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 30px auto;
  padding: 20px;
  gap: 40px;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormColumn = styled(motion.div)`
  flex: 1 1 500px;
`;

const Title = styled.h2`
  font-size: 2rem;
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  margin-bottom: 30px;
  color: #444;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputRow = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const FieldGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 5px;
`;

const Input = styled.input`
  padding: 12px 15px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 1rem;
`;

const ErrorMsg = styled.span`
  font-size: 0.85rem;
  color: red;
`;

const OptionRow = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`;

const OptionBox = styled.label`
  flex: 1;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  font-size: 0.95rem;
  background-color: ${(props) => (props.active ? props.theme.colors.secondary : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#333")};
  transition: all 0.2s;
  text-align: center;
  box-shadow: ${(props) => (props.active ? "0 0 0 3px rgba(0,0,0,0.1)" : "none")};

  input {
    display: none;
  }

  &:hover {
    border-color: ${(props) => props.theme.colors.secondary};
  }
`;

const Checkbox = styled.label`
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  line-height: 1.4;

  input {
    margin-top: 0;
    flex-shrink: 0;
  }

  span {
    display: inline;
    white-space: nowrap;
  }

  a {
    color: ${(props) => props.theme.colors.secondary || "#2e9694"};
    text-decoration: underline;
    font-weight: 500;
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 4px;
  }
`;


const Button = styled.button`
  background: ${(props) => props.theme.colors.secondary};
  color: #fff;
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(props) => props.theme.colors.primary};
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const SideColumn = styled(motion.div)`
  flex: 1 1 500px;
  display: flex;
  flex-direction: column;
  justify-content: center;
   background-color: ${(props) => props.theme.colors.extralight};
  border-radius: 12px;
  padding: 0; /* removed padding */
  text-align: center;
  overflow: hidden; /* ensures no overflow spacing */

  img {
    max-width: 100%;
    border-radius: 0; /* remove inner img radius to prevent spacing */
    margin-bottom: 0; /* remove margin under image */
  }
`;


const ImageWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;


const OverlayText = styled.div`
  margin-top: 12px;
  padding: 20px;
  border-radius: 12px;
  background: ${(props) => props.theme.colors.extralight};
  color: ${(props) => props.theme.colors.medium};
  font-size: 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 10px;

  a {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: underline;
  }

  span {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  span div {
    display: flex;
    flex-direction: column;
  }

  svg {
    color: ${(props) => props.theme.colors.secondary};
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 4px;
  }
`;


const SuccessMsg = styled.p`
  font-size: 1rem;
  color: green;
`;
const TextArea = styled.textarea`
  padding: 12px 15px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 1rem;
  resize: vertical;
`;
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Popup = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${(props) => (props.error ? "#f44336" : "#4caf50")}; // ✅ red for error, green for success
  color: #fff;
  padding: 14px 24px;
  border-radius: 10px;
  box-shadow: 0px 6px 16px rgba(0,0,0,0.25);
  animation: ${fadeIn} 0.4s ease-out;
  font-weight: 500;
  font-size: 15px;
  z-index: 9999;
  min-width: 280px;
  text-align: center;
`;



const ContactUs = () => {
  const { control, register, handleSubmit, watch, formState: { errors, isSubmitting }, reset, setValue } = useForm(); const selectedOption = watch("userType");
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

const onSubmit = async (data) => {
  const whatsappNumber = "919876543210";

  const message = `
📩 *New Contact Form Submission*

👤 Name: ${data.firstName} ${data.lastName || ""}
📧 Email: ${data.email}
📞 Phone: ${data.phone}
🏢 Company: ${data.company || "N/A"}

📝 Message:
${data.message}
  `;

  const encodedMessage = encodeURIComponent(message);

  const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

  // ✅ Open WhatsApp in new tab
  window.open(whatsappURL, "_blank");

  // Optional UI feedback
  setSubmitStatus("✅ Redirecting to WhatsApp...");
  setShowPopup(true);
  reset();
  setTimeout(() => setShowPopup(false), 3000);
};


  // const onSubmit = async (data) => {
  //   try {
  //     const result = await emailjs.send(
  //       "service_6wcs9c5",   // ✅ Service ID
  //       "template_e3tds9d",           // ✅ Template ID (the one in screenshot)
  //       {
  //         name: `${data.firstName} ${data.lastName || ""}`, // maps to {{name}}
  //         email: data.email,                                // maps to {{email}}
  //         message: `
  //         📞 Phone: ${data.phone}
  //         🏢 Company: ${data.company || "N/A"}
  //         📝 Message: ${data.message}
  //       `,
  //         subject: "New Contact Form Submission",           // maps to {{subject}}
  //       },
  //       "hiXr_WrnAIOEupIP0" // ✅ Public key (no trailing dot!)
  //     );

  //     console.log("✅ Email successfully sent!", result.text);
  //     setSubmitStatus("✅ Thank you! Your message has been sent.");
  //     setShowPopup(true); // open popup
  //     reset();
  //     setTimeout(() => setShowPopup(false), 3000); // auto close
  //   } catch (error) {
  //     console.error("❌ Email sending failed:", error);
  //     setSubmitStatus("❌ Failed to send message. Try again later.");
  //     setShowPopup(true);
  //     setTimeout(() => setShowPopup(false), 3000);
  //   }
  // };

  return (
    <>
      <Helmet>
        <title>Contact LK Education | Get in Touch</title>
        <meta
          name="description"
          content="Get in touch with LK Education for electrical engineering solutions, project collaborations, and technical support."
        />
      </Helmet>|
      <Container>
        <FormColumn
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>Connect With Us:</Title>
          {/* <Subtitle>We would love to hear from you.</Subtitle> */}

          {showPopup && (
            <Popup error={submitStatus.startsWith("❌")}>
              {submitStatus}
            </Popup>
          )}
          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <InputRow>
              <FieldGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input id="firstName" type="text" {...register("firstName", { required: "First name is required" })} placeholder="First name" />
                {errors.firstName && <ErrorMsg>{errors.firstName.message}</ErrorMsg>}
              </FieldGroup>

              <FieldGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input id="lastName" type="text" {...register("lastName")} placeholder="Last name (optional)" />
                {/* {errors.lastName && <ErrorMsg>{errors.lastName.message}</ErrorMsg>} */}
              </FieldGroup>
            </InputRow>

            <FieldGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...register("email", {
                  required: "Email is required",
                  minLength: {
                    value: 6,
                    message: "Email must be at least 6 characters long",
                  },
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // ✅ Basic email regex
                    message: "Enter a valid email address",
                  },
                })}
                placeholder="YourEmail@gmail.com"
              />
              {errors.email && <ErrorMsg>{errors.email.message}</ErrorMsg>}
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="phone">Phone</Label>

              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone number is required",
                  validate: (value) =>
                    isValidPhoneNumber("+" + value) || "Enter a valid phone number",
                }}
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    country={"in"}
                    enableSearch={true}
                    inputStyle={{
                      width: "100%",
                      paddingLeft: "48px",
                      paddingTop: "12px",
                      paddingBottom: "12px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      fontSize: "1rem",
                    }}
                    buttonStyle={{
                      border: "none",
                      background: "transparent",
                    }}
                    onChange={(phone) => field.onChange(phone)}
                  />
                )}
              />

              {errors.phone && <ErrorMsg>{errors.phone.message}</ErrorMsg>}
            </FieldGroup>


            <FieldGroup>
              <Label htmlFor="company">Company Name</Label>
              <Input id="company" type="text" {...register("company")} placeholder="Company Name(optional)" />
            </FieldGroup>

            {errors.userType && <ErrorMsg>{errors.userType.message}</ErrorMsg>}
            <FieldGroup>
              <Label htmlFor="message">Message</Label>
              <TextArea id="message" rows="4" placeholder="Your message..." {...register("message", { required: "Message is required" })} />
              {errors.message && <ErrorMsg>{errors.message.message}</ErrorMsg>}
            </FieldGroup>

            <Checkbox>
              <input
                type="checkbox"
                {...register("agree", { required: "You must agree to the privacy policy" })}
              />
              <span>
                I agree to the&nbsp;
                <a
                  href="/privacypolicy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </span>
            </Checkbox>
            {errors.agree && <ErrorMsg>{errors.agree.message}</ErrorMsg>}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Send Your Message"}
            </Button>
          </StyledForm>
        </FormColumn>

        <SideColumn
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
<ImageWrapper>
  <img
    src="https://cdn.shopify.com/s/files/1/0851/4607/5431/files/Tally_image_2_34e7aef6-9f23-40f0-b2b4-cae7db353f30.png?v=1765456259"
    alt="Location"
  />
</ImageWrapper>

<OverlayText>
  <span>
    <FaMapMarkerAlt />
    <div>
      <div>182, S. N. High Road, Sripuram, Tirunelveli Junction – 627001</div>
    
    </div>
  </span>

  <span><FaPhoneAlt /> <a href="tel:+979488687563">+97 9488687563</a></span>
  <span><FaEnvelope /> <a href="mailto:lkeduganesh@gmail.com">lkeduganesh@gmail.com</a></span>
  <span><FaClock /> Mon–Sat 10:00–19:00 | Sunday closed</span>
</OverlayText>

        </SideColumn>
      </Container>
    </>
  );
};

export default ContactUs;
